import { useMemo } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import {
  type FrameSize,
  computePhotoFillCoveredSize,
  computePhotoFillFrameSize,
} from '../helpers/photo-fill-frame'
import type { LayoutBox } from '../helpers/shape-shell-layout'

export interface PhotoFillSizing {
  /** Cover-fit frame: the larger of the shape's declared box and the shell bounds. */
  frameSize: FrameSize
  /** The photo's actual rendered box; null with no photo fill or an unknown natural size. */
  coveredSize: ReturnType<typeof computePhotoFillCoveredSize>
}

/** Memoized photo-fill frame and covered size (see `helpers/photo-fill-frame.ts`). */
export function usePhotoFillSizing(shape: ShapeData, shellBounds: LayoutBox): PhotoFillSizing {
  const { width, height, photoFill } = shape

  const frameSize = useMemo(
    () =>
      computePhotoFillFrameSize(
        { width, height },
        { width: shellBounds.width, height: shellBounds.height }
      ),
    [width, height, shellBounds.width, shellBounds.height]
  )

  const coveredSize = useMemo(
    () => computePhotoFillCoveredSize(photoFill, frameSize),
    [photoFill, frameSize]
  )

  return { frameSize, coveredSize }
}
